"use client";

import { useState, useMemo, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Search, ChevronRight, X, SlidersHorizontal, Package,
  Grid3X3, List, Home,
} from 'lucide-react';
import {
  getProducts,
  getProductsByCategory,
  searchProducts as searchProductsService,
} from '@/services/productService';
import { filterProductsByAccess } from '@/services/productAccessService';
import { useAuth } from '@/context/AuthContext';
import { getDisplayPrice } from '@/services/pricingService';
import { useVehicleSelection } from '@/context/VehicleContext';
import { VehicleSelectionBar } from '@/components/vehicle/VehicleSelectionBar';
import { getProductTypeLabel } from '@/utils/productType';
import { LeftSidebar } from '@/components/category/LeftSidebar';
import { ProductListItem } from '@/components/category/ProductListItem';
import { ProductGridCard } from '@/components/category/ProductGridCard';
import { SubCategoryGrid } from '@/components/category/SubCategoryGrid';
import { PageSkeleton } from '@/components/category/PageSkeleton';

const PAGE_SIZE = 12;
const PRODUCT_TYPE_OPTIONS = ['OEM', 'OES', 'AFTERMARKET'];

const flattenHierarchy = (nodes = []) => {
  const flat = [];
  const stack = Array.isArray(nodes) ? [...nodes] : [];
  while (stack.length) {
    const node = stack.pop();
    if (!node) continue;
    flat.push(node);
    if (Array.isArray(node.children) && node.children.length) {
      node.children.forEach((child) => stack.push(child));
    }
  }
  return flat;
};

export default function CategoryClient({ slug, initialData }) {
  const searchParams = useSearchParams();
  const queryFromParams = searchParams?.get('search') || '';
  const searchQuery = queryFromParams || initialData?.searchQuery || '';

  const { user } = useAuth();
  const { selection, isActive } = useVehicleSelection();
  const vehicleKey = (selection?.vehicleIds || []).join(',');

  const [sortBy, setSortBy] = useState('relevance');
  const [filterType, setFilterType] = useState([]);
  const [inlineSearch, setInlineSearch] = useState('');
  const [viewMode, setViewMode] = useState('list');

  const [products, setProducts] = useState(initialData?.products || []);
  const [productSummary, setProductSummary] = useState(initialData?.productSummary || { total: 0, counts: [] });
  const [page, setPage] = useState(initialData?.page || 1);
  const [hasMore, setHasMore] = useState(Boolean(initialData?.hasMore));
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [category] = useState(initialData?.category || null);
  const [childCategories] = useState(initialData?.childCategories || []);
  const [siblingCategories] = useState(initialData?.siblingCategories || []);
  const [parentCategory] = useState(initialData?.parentCategory || null);
  const [rootCategories] = useState(initialData?.rootCategories || []);
  const [categoryHierarchy] = useState(initialData?.categoryHierarchy || []);
  const [breadcrumb] = useState(initialData?.breadcrumb || []);
  const [resolvedCategoryId] = useState(initialData?.resolvedCategoryId || '');

  const fetchPage = useCallback(async ({ targetPage, append = false } = {}) => {
    const vehicleIds = selection?.vehicleIds || [];
    const productType = filterType.length > 0 ? filterType.join(',') : undefined;
    let list = [];

    if (resolvedCategoryId) {
      list = await getProducts({ categoryId: resolvedCategoryId, vehicleIds, productType, page: targetPage, limit: PAGE_SIZE });
    } else if (slug) {
      list = await getProductsByCategory(slug, { vehicleIds, productType, page: targetPage, limit: PAGE_SIZE });
    } else if (searchQuery) {
      list = await searchProductsService(searchQuery, { vehicleIds, productType, page: targetPage, limit: PAGE_SIZE });
    } else {
      list = await getProducts({ vehicleIds, productType, page: targetPage, limit: PAGE_SIZE });
    }

    const items = Array.isArray(list) ? list : [];
    setProducts((prev) => (append ? [...prev, ...items] : items));
    setHasMore(items.length === PAGE_SIZE);
    setPage(targetPage);

    const meta = list?.meta || {};
    setProductSummary({
      total: Number(meta.total || items.length || 0),
      counts: Array.isArray(meta.counts) ? meta.counts : [],
    });
  }, [selection?.vehicleIds, filterType, resolvedCategoryId, slug, searchQuery]);

  useEffect(() => {
    let active = true;
    const hasVehicleFilter = Array.isArray(selection?.vehicleIds) && selection.vehicleIds.length > 0;
    const hasProductTypeFilter = filterType.length > 0;

    if (!hasVehicleFilter && !hasProductTypeFilter) {
      setProducts(initialData?.products || []);
      setProductSummary(initialData?.productSummary || { total: 0, counts: [] });
      setHasMore(Boolean(initialData?.hasMore));
      setPage(initialData?.page || 1);
      setLoadingProducts(false);
      return undefined;
    }

    const load = async () => {
      setLoadingProducts(true);
      setHasMore(true);
      setPage(1);
      try {
        await fetchPage({ targetPage: 1, append: false });
      } catch (_e) {
        if (active) {
          setProducts([]);
          setHasMore(false);
        }
      } finally {
        if (active) setLoadingProducts(false);
      }
    };

    load();
    return () => { active = false; };
  }, [fetchPage, filterType, vehicleKey, selection?.vehicleIds, initialData]);

  const filteredProducts = useMemo(() => {
    let result = filterProductsByAccess(products, user);

    if (inlineSearch.trim()) {
      const q = inlineSearch.toLowerCase();
      result = result.filter((p) =>
        p.name?.toLowerCase().includes(q) ||
        p.manufacturerBrand?.toLowerCase().includes(q) ||
        p.oemNumber?.toLowerCase().includes(q) ||
        p.oesNumber?.toLowerCase().includes(q) ||
        p.sku?.toLowerCase().includes(q)
      );
    }

    const getPrice = (p) => getDisplayPrice(p, user).price || 0;
    if (sortBy === 'price-low') result = [...result].sort((a, b) => getPrice(a) - getPrice(b));
    if (sortBy === 'price-high') result = [...result].sort((a, b) => getPrice(b) - getPrice(a));
    if (sortBy === 'name-az') result = [...result].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    if (sortBy === 'name-za') result = [...result].sort((a, b) => (b.name || '').localeCompare(a.name || ''));

    return result;
  }, [products, sortBy, inlineSearch, user]);

  const clearFilters = () => { setFilterType([]); setInlineSearch(''); setSortBy('relevance'); };
  const handleFilterToggle = (type, checked) => setFilterType((prev) => checked ? [...prev, type] : prev.filter((t) => t !== type));

  const activeFiltersCount = filterType.length + (inlineSearch ? 1 : 0);
  const hasChildren = childCategories.length > 0;
  const hasProducts = filteredProducts.length > 0;

  const hierarchyFlat = useMemo(() => flattenHierarchy(categoryHierarchy), [categoryHierarchy]);
  const fallbackParent = breadcrumb.length >= 2 ? breadcrumb[breadcrumb.length - 2] : null;
  const fallbackParentSlug = fallbackParent?.slug || '';

  const fallbackSiblingCategories = useMemo(() => {
    if (!fallbackParentSlug || hierarchyFlat.length === 0) return [];
    const parentNode = hierarchyFlat.find((node) => String(node?.slug || '') === String(fallbackParentSlug));
    return Array.isArray(parentNode?.children) ? parentNode.children : [];
  }, [fallbackParentSlug, hierarchyFlat]);

  const effectiveSidebarCategories =
    childCategories.length > 0
      ? childCategories
      : siblingCategories.length > 0
        ? siblingCategories
        : fallbackSiblingCategories.length > 0
          ? fallbackSiblingCategories
          : rootCategories;

  const resolvedSidebarParentName = parentCategory?.name || fallbackParent?.name || '';
  const effectiveSidebarTitle =
    childCategories.length > 0
      ? 'Sub-categories'
      : resolvedSidebarParentName || 'Categories';

  const selectedProductTypeTotal = filterType.length
    ? filterType.reduce((sum, type) => {
      const match = productSummary.counts.find((item) => item.key === type);
      return sum + Number(match?.count || 0);
    }, 0)
    : productSummary.total;

  const displayedProductTotal = inlineSearch.trim()
    ? filteredProducts.length
    : (filterType.length ? selectedProductTypeTotal : productSummary.total);

  if (!initialData) return <PageSkeleton />;

  return (
    <Layout>
      <div className="border-b border-border/50 bg-muted/20">
        <div className="container mx-auto px-4 py-3">
          <nav className="flex items-center gap-1.5 text-sm text-muted-foreground flex-wrap overflow-x-auto whitespace-nowrap no-scrollbar">
            <Link href="/" className="hover:text-primary transition-colors flex items-center gap-1">
              <Home className="w-3.5 h-3.5" />
            </Link>
            <ChevronRight className="w-3.5 h-3.5 opacity-40" />
            <Link href="/categories" className="hover:text-primary transition-colors">All Categories</Link>
            {breadcrumb.map((cat, i) => (
              <span key={cat._id || cat.id} className="flex items-center gap-1.5">
                <ChevronRight className="w-3.5 h-3.5 opacity-40" />
                {i === breadcrumb.length - 1 ? (
                  <span className="text-foreground font-medium">{cat.name}</span>
                ) : (
                  <Link href={`/categories/${cat.slug}`} className="hover:text-primary transition-colors">{cat.name}</Link>
                )}
              </span>
            ))}
            {!category && (
              <>
                <ChevronRight className="w-3.5 h-3.5 opacity-40" />
                <span className="text-foreground font-medium">{searchQuery ? `"${searchQuery}"` : 'All Parts'}</span>
              </>
            )}
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 py-5 md:py-7">
        <div className="mb-5">
          <VehicleSelectionBar title="Selected Vehicle" />
        </div>

        <div className="flex gap-5 lg:gap-6">
          <LeftSidebar
            currentSlug={slug}
            sidebarCategories={effectiveSidebarCategories}
            sidebarTitle={effectiveSidebarTitle}
            filterType={filterType}
            onToggleFilter={handleFilterToggle}
            products={products}
            productTypeCounts={productSummary.counts}
            clearFilters={clearFilters}
            showFilters={!hasChildren}
          />

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-foreground">
                  {category?.name || (searchQuery ? `Results for "${searchQuery}"` : 'All Parts')}
                </h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {loadingProducts
                    ? 'Loading products...'
                    : hasChildren
                      ? `${childCategories.length} sub-categories`
                      : hasProducts
                        ? `${displayedProductTotal} product${displayedProductTotal !== 1 ? 's' : ''} found`
                        : isActive
                          ? 'No compatible products for selected vehicle'
                          : 'No products yet'}
                </p>
              </div>
            </div>

            {hasChildren && (
              <div className="mb-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Sub-categories</p>
                <SubCategoryGrid categories={childCategories} />
              </div>
            )}

            {!hasChildren && (
              <>
                <div className="bg-card border border-border/50 rounded-xl px-3 py-2.5 mb-4">
                  <div className="flex flex-col sm:flex-row gap-2.5">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="Search products"
                        value={inlineSearch}
                        onChange={(e) => setInlineSearch(e.target.value)}
                        className="pl-9 pr-8 h-9 text-sm bg-muted/40 border-0 focus:bg-background focus:ring-2 focus:ring-primary/20 rounded-lg"
                      />
                      {inlineSearch && (
                        <button onClick={() => setInlineSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="hidden sm:flex items-center border border-border/50 rounded-lg overflow-hidden">
                        <button
                          onClick={() => setViewMode('list')}
                          className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                          title="List view"
                        >
                          <List className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setViewMode('grid')}
                          className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                          title="Grid view"
                        >
                          <Grid3X3 className="w-4 h-4" />
                        </button>
                      </div>

                      <Sheet>
                        <SheetTrigger asChild>
                          <Button variant="outline" size="icon" className="lg:hidden h-9 w-9 rounded-lg relative border-border/50">
                            <SlidersHorizontal className="w-4 h-4" />
                            {filterType.length > 0 && (
                              <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                                {filterType.length}
                              </span>
                            )}
                          </Button>
                        </SheetTrigger>
                        <SheetContent side="bottom" className="h-[60vh] rounded-t-3xl lg:hidden p-0">
                          <SheetHeader className="px-5 py-4 border-b border-border/50">
                            <div className="flex items-center justify-between">
                              <SheetTitle className="text-lg font-bold">Filters</SheetTitle>
                              {filterType.length > 0 && (
                                <button onClick={clearFilters} className="text-sm text-primary hover:underline">Clear All</button>
                              )}
                            </div>
                          </SheetHeader>
                          <div className="px-5 py-5 space-y-4">
                            <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Product Type</h4>
                            {PRODUCT_TYPE_OPTIONS.map((type) => (
                              <label key={type} className="flex items-center gap-3 cursor-pointer">
                                <Checkbox
                                  checked={filterType.includes(type)}
                                  onCheckedChange={(checked) => handleFilterToggle(type, checked)}
                                  className="border-2 data-[state=checked]:bg-primary data-[state=checked]:border-primary w-5 h-5"
                                />
                                <span className="text-base font-medium">{getProductTypeLabel(type)}</span>
                                <span className="ml-auto text-sm text-muted-foreground">{(products || []).filter((p) => p.productType === type).length}</span>
                              </label>
                            ))}
                          </div>
                        </SheetContent>
                      </Sheet>

                      <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger className="w-[130px] sm:w-[150px] h-9 text-sm rounded-lg border-border/50 focus:ring-2 focus:ring-blue-500 bg-background ">
                          <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent className="rounded-lg border border-zinc-200">
                          <SelectItem value="relevance">Relevance</SelectItem>
                          <SelectItem value="price-low">Price: Low to High</SelectItem>
                          <SelectItem value="price-high">Price: High to Low</SelectItem>
                          <SelectItem value="name-az">Name: A to Z</SelectItem>
                          <SelectItem value="name-za">Name: Z to A</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {activeFiltersCount > 0 && (
                    <div className="flex flex-wrap items-center gap-2 pt-2.5 mt-2.5 border-t border-border/30">
                      {inlineSearch && (
                        <button onClick={() => setInlineSearch('')} className="inline-flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-medium px-2.5 py-1 rounded-full hover:bg-primary/20 transition-colors">
                          &quot;{inlineSearch.length > 12 ? inlineSearch.slice(0, 12) + '...' : inlineSearch}&quot; <X className="w-3 h-3" />
                        </button>
                      )}
                      {filterType.map((type) => (
                        <button key={type} onClick={() => setFilterType((prev) => prev.filter((t) => t !== type))}
                          className="inline-flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-medium px-2.5 py-1 rounded-full hover:bg-primary/20 transition-colors">
                          {getProductTypeLabel(type)} <X className="w-3 h-3" />
                        </button>
                      ))}
                      <button onClick={clearFilters} className="text-xs text-muted-foreground hover:text-destructive ml-1 transition-colors">
                        Clear all
                      </button>
                    </div>
                  )}
                </div>

                {loadingProducts && products.length === 0 ? (
                  <div className="space-y-3 animate-pulse">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="h-32 bg-muted rounded-xl" />
                    ))}
                  </div>
                ) : filteredProducts.length === 0 ? (
                  <div className="text-center py-16 bg-card rounded-xl border border-border/50">
                    {activeFiltersCount > 0 || inlineSearch ? (
                      <Search className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                    ) : (
                      <Package className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                    )}
                    <h3 className="text-lg font-semibold mb-1">
                      {activeFiltersCount > 0 || inlineSearch
                        ? isActive ? 'No compatible products' : 'No matching products'
                        : 'No parts found'}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                      {activeFiltersCount > 0 || inlineSearch
                        ? isActive ? 'Try changing your vehicle or remove filters.' : 'Try adjusting your search or removing filters.'
                        : "We couldn't find any parts in this category."}
                    </p>
                    <div className="flex items-center justify-center gap-3 flex-wrap">
                      {(activeFiltersCount > 0 || inlineSearch) && (
                        <Button variant="outline" onClick={clearFilters} className="rounded-lg">Clear All Filters</Button>
                      )}
                      <Link href="/categories">
                        <Button className="rounded-lg">Browse Categories</Button>
                      </Link>
                    </div>
                  </div>
                ) : viewMode === 'list' ? (
                  <div className="space-y-3">
                    {filteredProducts.map((p, index) => (
                      <ProductListItem key={p._id || p.id} product={p} isAboveFold={index < 3} />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
                    {filteredProducts.map((p, index) => (
                      <ProductGridCard key={p._id || p.id} product={p} isAboveFold={index < 4} />
                    ))}
                  </div>
                )}

                {hasMore && filteredProducts.length > 0 && (
                  <div className="text-center mt-8">
                    <Button
                      variant="outline"
                      size="lg"
                      className="rounded-lg min-w-[200px]"
                      disabled={loadingMore}
                      onClick={async () => {
                        if (loadingMore || loadingProducts || !hasMore) return;
                        setLoadingMore(true);
                        try { await fetchPage({ targetPage: page + 1, append: true }); }
                        finally { setLoadingMore(false); }
                      }}
                    >
                      {loadingMore ? 'Loading...' : 'Show More Products'}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
